import classnames from 'classnames';
import { COLORS } from '../constants';
import { ColorsType, FillsType, SizesType, TextColorsType } from '../types';
import classes from '../index.module.scss';
import {
    MONOCHROME_BUTTONS,
    COLOR_MAP,
    GHOST_COLOR_WEIGHT_MAP,
    ICON_COLOR_WEIGHT_MAP,
    BACKGROUND_COLOR_MAP,
    BACKGROUND_COLOR_WEIGHT_MAP
} from './constants';

/**
 * Calculates the button's appropriate disabled text color weight.
 */
const disabledColorWeight = (
    isGhost: boolean,
    isMonochromaticButton: boolean
) => {
    if (isGhost) {
        return isMonochromaticButton ? 500 : 300;
    }

    if (isMonochromaticButton) {
        return 600;
    }

    return 100;
};

/**
 * Calculates the button's appropriate text color.
 */
const buttonTextColor = (
    color: ColorsType,
    textColor: TextColorsType,
    disabled: boolean,
    isGhost: boolean,
    isWhite: boolean,
    isMonochromaticButton: boolean
) => {
    const baseColor = COLOR_MAP[color] || color;
    const baseTextColor = COLOR_MAP[textColor] || textColor;
    const ghostColorWeight = GHOST_COLOR_WEIGHT_MAP[color] || 700;

    if (disabled) {
        return `${baseColor}-${disabledColorWeight(
            isGhost,
            isMonochromaticButton
        )}`;
    }

    if (color === 'light-grey') {
        return `${COLORS.GREY}-1100`;
    }

    if (isWhite && !isGhost) {
        return `${baseTextColor}-${textColor === 'dark-grey' ? 1000 : 700}`;
    }

    if (!isWhite && isGhost) {
        return `${baseColor}-${ghostColorWeight}`;
    }

    return COLORS.WHITE;
};

/**
 * Calculates the button's appropriate background color weight.
 */
const backgroundColorWeight = (
    color: ColorsType,
    disabled: boolean,
    isMonochromaticButton: boolean
) => {
    if (disabled) {
        return isMonochromaticButton ? 200 : 300;
    }

    return BACKGROUND_COLOR_WEIGHT_MAP[color] || 700;
};

/**
 * Calculates the button's appropriate background color.
 */
const backgroundColor = (
    color: ColorsType,
    disabled: boolean,
    isWhite: boolean,
    isMonochromaticButton: boolean
) => {
    const baseColor = BACKGROUND_COLOR_MAP[color] || color;
    const weight = backgroundColorWeight(
        color,
        disabled,
        isMonochromaticButton
    );

    if (isWhite) {
        return COLORS.WHITE;
    }

    if (!disabled && color === 'black') {
        return COLORS.BLACK;
    }

    return `${baseColor}-${weight}`;
};

/**
 * Calculates the appropriate icon color.
 */
const iconColor = (
    color: ColorsType,
    isGhost: boolean
) => {
    const baseColor = COLOR_MAP[color] || color;
    const baseColorWeight = ICON_COLOR_WEIGHT_MAP[color] || 700;

    if (color === 'light-grey') {
        return `${COLORS.GREY}-1000`;
    }

    if (isGhost) {
        return `${baseColor}-${baseColorWeight}`;
    }

    return COLORS.WHITE;
};

/**
 * Generates the component's CSS classes.
 */
const generateClasses = (
    color: ColorsType,
    textColor: TextColorsType,
    disabled: boolean,
    fill?: FillsType,
    size?: SizesType,
    loading?: boolean,
    className?: string
) => {
    const isGhost = fill === 'ghost';
    const isWhite = color === 'white';
    const isMonochromaticButton = MONOCHROME_BUTTONS.includes(color);

    const mainClasses = classnames(
        classes.button,
        classes[`color-${color}`],
        classes[`fill-${fill}`],
        classes[`size-${size}`],
        `co-${buttonTextColor(
            color,
            textColor,
            disabled,
            isGhost,
            isWhite,
            isMonochromaticButton
        )}`,
        className,
        {
            [classes.loading]: loading,
            [classes.disabled]: disabled,
            [classes[`text-color-${textColor}`]]: isWhite,
            [`bg-co-${backgroundColor(
                color,
                disabled,
                isWhite,
                isMonochromaticButton
            )}`]: !isGhost
        }
    );

    const iconClasses = classnames(
        classes.loadingIcon,
        `co-${iconColor(
            color,
            isGhost
        )}`
    );

    return {
        main: mainClasses,
        icon: iconClasses
    };
};

export default generateClasses;
